// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { getPageLinkByNodeId, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xGoogleAnalyticsState } from "@werner/App/GoogleAnalyticsConnector/Store/Reducers";
import { getDataLayer } from "@werner/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import CellPhone from "@werner/XDL/Icons/CellPhone";
import Email from "@werner/XDL/Icons/Email";
import { createLinkIconStyles } from "@werner/XDL/Utilities";
import * as cssLinter from "css";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    links = "links",
    customCSS = "customCSS",
}

// <XNG-Changes>
interface LinkListLinkModel {
    fields: {
        openInNewWindow: boolean;
        overriddenTitle: string;
        destination: LinkFieldValue;
        type: "link" | "phone" | "email";
    };
}
// </XNG-Changes>

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkListLinkModel[];
        [fields.customCSS]: string;
    };
    extendedStyles?: HeaderLinkListStyles;
}

const mapStateToProps = (state: xGoogleAnalyticsState) => ({
    xGoogleAnalytics: state.xGoogleAnalytics,
    isAuthenticated: xIsAuthenticated(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface HeaderLinkListStyles {
    headerLinkListWrapper?: InjectableCss;
    link?: LinkPresentationProps;
}

export const headerLinkListStyles: HeaderLinkListStyles = {
    headerLinkListWrapper: {
        css: css`
            text-align: right;
            padding: 0;
            margin-right: 0;
        `,
    },
    link: {
        typographyProps: {
            size: 14,
            weight: 500,
            css: css`
                display: inline-block;
            `,
        },
        css: css`
            margin: 0 0 0 50px;
            display: inline-block;
        `,
    },
};

const defaultCustomCss = `.list-wrapper {
}

.list-link {
}
`;

// <XNG-Changes>
const iconsMap = {
    phone: CellPhone,
    email: Email,
};

// </XNG-Changes>

export const HeaderLinkList: React.FC<Props> = ({ fields, extendedStyles, xGoogleAnalytics, isAuthenticated }) => {
    const { links, hasMissingCategories } = useGetLinks(fields.links, o => o.fields.destination);

    const styles = useMergeStyles("headerLinkList", headerLinkListStyles, extendedStyles);

    if (hasMissingCategories) {
        return null;
    }
    
    if (links.length < 1) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    // Xng-Change
    // WES-1768
    const sendTracking = (link: PageLinkModel) => {
        const dataLayer = getDataLayer();

        if (!dataLayer || !xGoogleAnalytics.toolBarLinkEvent) {
            return;
        }

        const googleAnalyticsData = {
            event: xGoogleAnalytics.toolBarLinkEventName,
            click_text: link.title,
            destination: link.url,
            affiliation: xGoogleAnalytics.affiliation,
            authentication_state: isAuthenticated ? "authenticated" : "unauthenticated",
        };

        dataLayer.push(googleAnalyticsData);
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="list-wrapper" {...styles.headerLinkListWrapper}>
                {links.map((link, index) => {
                    if (!link.url) {
                        return null;
                    }
                    const linkFields = fields.links[index].fields;
                    const linkStyles =
                        !linkFields.type || linkFields.type === "link"
                            ? styles.link
                            : createLinkIconStyles(iconsMap[linkFields.type], styles.link);

                    return (
                        <Link
                            className="list-link"
                            key={`${link.url}.${link.title}`}
                            href={link.url}
                            target={linkFields.openInNewWindow ? "_blank" : ""}
                            aria-label={linkFields.overriddenTitle || link.title}
                            {...linkStyles}
                            onClick={() => sendTracking(link as PageLinkModel)}
                        >
                            {/* XNG-Changes: add overriddenTitle */}
                            {linkFields.overriddenTitle || link.title}
                        </Link>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(HeaderLinkList),
    definition: {
        group: "Header",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    // XNG-Changes: add overriddenTitle
                    {
                        name: "overriddenTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: "type",
                        displayName: "Link Type",
                        editorTemplate: "DropDownField",
                        options: [
                            {
                                displayName: "Link",
                                value: "link",
                            },
                            {
                                displayName: "Phone",
                                value: "phone",
                            },
                            {
                                displayName: "E-Mail",
                                value: "email",
                            },
                        ],
                        defaultValue: "link",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
